import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { formatCurrency } from "@/lib/utils";
import { Product } from "@/types";
import { PermissionGate } from "@/components/common";
import { Switch } from "@radix-ui/react-switch";
import { Coffee } from "lucide-react";
import { Link } from "react-router-dom";
import { ProductRowMenu } from "./ProductRowMenu";

interface ListRowProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onToggle: (id: string) => void;
  onView: (id: string) => void;
}

export function ProductsTable({
  products,
  onEdit,
  onDelete,
  onToggle,
  onView,
}: ListRowProps) {
  return (
    <div className="hidden lg:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Variants</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            const defaultVariant =
              product.variants?.find((v) => v.isDefault) ||
              product.variants?.[0];

            return (
              <TableRow key={product.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="h-10 w-10 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                        <Coffee className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <Link
                        to={`/products/${product.id}`}
                        className="block font-medium underline-offset-2 hover:text-primary hover:underline"
                      >
                        {product.name}
                      </Link>
                      {product.description && (
                        <p className="line-clamp-1 text-xs text-muted-foreground">
                          {product.description}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {product.sku}
                </TableCell>
                <TableCell>
                  {product.category ? (
                    <Badge variant="outline">{product.category.name}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {defaultVariant ? formatCurrency(defaultVariant.price) : "-"}
                </TableCell>
                <TableCell>{product.variants?.length || 0}</TableCell>
                <TableCell>
                  <PermissionGate
                    permission="products.update"
                    fallback={
                      <Badge
                        variant={product.isActive ? "outline" : "secondary"}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </Badge>
                    }
                  >
                    <Switch
                      checked={product.isActive}
                      onCheckedChange={() => onToggle(product.id)}
                    />
                  </PermissionGate>
                </TableCell>
                <TableCell>
                  <ProductRowMenu
                    product={product}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
